import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Link as MuiLink,
  makeStyles,
} from "@material-ui/core";
import { Form, TextControl } from "../Utils/Control";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { requestPasswordReset } from "../../Service/UserFactory";
import { useState } from "react";
import { EMAIL_REGEX } from "../../assets/data/Other";
import PropTypes from "prop-types";
import  {  useEffect } from "react";
const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      margin: 0,
      padding: 0,
      backgroundColor: "#fff",
    },
  },
  root: {
    background: "#fff",
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 3,
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.12s linear",
    "&.active": { opacity: 1, pointerEvents: "all" },
  },

  card: {
    padding: theme.spacing(4, 5),
    maxWidth: 328,
    width: "100%",
    borderRadius: 14,
    backgroundColor: "#fff",
    textAlign: "center",
    border: "0.3px solid rgba(0, 0, 0, 0.15)", 
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)", 
  },

  welcomeText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "1.4rem",
    marginBottom: theme.spacing(0.6),
  },
  subtitle: {
    color: "#333",
    marginBottom: theme.spacing(2),
    fontSize: "1rem",
  },
  emailField: {
    marginBottom: theme.spacing(0.5),
    textAlign: "left",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
      height: 44,
      "& fieldset": {
        border: "1.2px solid #ccc",
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  label: {
    display: "block",
    marginBottom: theme.spacing(0.5),
    fontWeight: 500,
    fontSize: "0.9rem",
    color: "#333",
  },
  forgotAndButton: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  linkText: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.4),
    color: theme.palette.primary.main,
    textDecoration: "none",
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  submitBtn: {
    padding: "8px 20px",
    borderRadius: 10,
    fontWeight: 500,
    fontSize: "0.9rem",
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  discoverSection: {
    marginTop: theme.spacing(2),
  },
  discoverInlineLinks: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(3),
  },
  discoverTitle: {
    fontWeight: 400,
    fontSize: "1rem", 
    marginBottom: theme.spacing(1),
   color: "#000",
  },
  footer: {
   marginTop: theme.spacing(3),
    textAlign: "center",
    color: "#000", 
    fontFamily: "Roboto, sans-serif",
    fontWeight: 400,
    fontSize: "0.8rem", 
    lineHeight: "100%",
    letterSpacing: "0%",
  },
  footerLinks: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      fontSize: "0.8rem",
      display: "inline",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
}));

export default function ForgotPassword({ show, login }) {
  const classes = useStyles();
  const { handleSubmit, control, reset } = useForm({
    mode: "onSubmit",
    defaultValues: {
      email: ""
    },
    reValidateMode: "onSubmit"
  });
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (show) {
      reset({ email: "" });
    }
  }, [show, reset]);


  const onSubmit = async (data) => {
    if (!loading) {
      setLoading(true);
      const status = await requestPasswordReset(data);
      setLoading(false);
      if (status) {

        reset({ email: "" });
        if (typeof login === "function") {
          login();
        } else {
          history.push("/login");
        }
      }
    }
  };

  const handleBackToSignIn = () => {
    reset({ email: "" });
    if (typeof login === "function") {
      login();
    } else {
      history.push("/login");
    }
  };

  return (
    <Box className={`${classes.root} ${show ? "active" : ""}`}>
      <Box className={classes.card}>
        <Typography className={classes.welcomeText}>Welcome to Falcon</Typography>
        <Typography className={classes.subtitle}>Let’s reset your password</Typography>

        <Form
          control={control}
          autoComplete="off"
          rules={{
            email: {
              pattern: {
                value: EMAIL_REGEX,
                message: "Invalid email address.",
              },
              required: "Email is required.",
            },
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box className={classes.emailField}>
            <label htmlFor="email" className={classes.label}>
              Email
            </label>
            <TextControl
              id="email"
              type="email"
              name="email"
              size="small"
              variant="outlined"
              fullWidth
              data-test="reset-email-field"
              label=" "
            />
          </Box>

          <Box className={classes.forgotAndButton}>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <MuiLink
                component="button"
                className={classes.linkText}
                onClick={handleBackToSignIn}
              >
                Back to Sign In?
              </MuiLink>
              <RouterLink to="/signup" className={classes.linkText}>
                Sign up for the demo
              </RouterLink>
            </Box>

            <Button
              variant="contained"
              className={classes.submitBtn}
              type="submit"
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : "Reset"}
            </Button>
          </Box>

        </Form>

        <Box className={classes.discoverSection}>
          <Typography className={classes.discoverTitle}>Discover</Typography>
          <RouterLink
            to="/whats-new"
            className={classes.linkText}
            style={{ display: "block", marginBottom: 4 }}
          >
            What’s new in Falcon?
          </RouterLink>
          <Box className={classes.discoverInlineLinks}>
            <RouterLink to="/offerings" className={classes.linkText}>
              CNCM Offerings
            </RouterLink>
            <RouterLink to="/blog" className={classes.linkText}>
              Blog
            </RouterLink>
          </Box>
        </Box>

        <Box className={classes.footer}>
          Copyright © 2025 CNCM LLC
          <Box className={classes.footerLinks}>
            <MuiLink component={RouterLink} to="/privacy-policy">
              Privacy Policy
            </MuiLink>
            <span>·</span>
            <MuiLink component={RouterLink} to="/terms">
              Terms of Service
            </MuiLink>
            <span>·</span>
            <MuiLink component={RouterLink} to="/contact">
              Contact
            </MuiLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

ForgotPassword.propTypes = {
  show: PropTypes.bool,
  login: PropTypes.func,
};
